import React, { useState, useRef, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  customStyle,
  fixedWidthCustomStyle,
  statusOptions,
} from "components/Common/constants";
import CommonTable from "components/ReactTable/Table";
import { useNavigate } from "react-router-dom";
import RightPanelCard from "components/Common/RightPanelCard";
import { useDispatch, useSelector } from "react-redux";
import {
  addCustomer,
  getCustomerList,
  getPaymentTermList,
} from "actions/customer";
import { customerList } from "constants/customer";
import { DEFAULT_RECORDS_PER_PAGE, REGEX } from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";

const initialFields = {
  firstName: {
    value: "",
    error: "",
  },
  lastName: {
    value: "",
    error: "",
  },
  email: {
    value: "",
    error: "",
  },
  contact: {
    value: "",
    error: "",
  },
  address: {
    value: "",
    error: "",
  },
  vatID: {
    value: "",
    error: "",
  },
  paymentTerm: {
    value: null,
    error: "",
  },
  clientID: {
    value: "",
    error: "",
  },
  companyName: {
    value: "",
    error: "",
  },
};

function CustomerList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customersList = useSelector((state) => state.customer.customerList);
  const paymentTermOptions = useSelector(
    (state) => state.customer.paymentTermList?.data?.paymentTerm || [],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchPaymentTerm: null,
    searchStatus: null,
    filterParams: [],
  });
  const notificationAlertRef = useRef(null);
  const [pageNum, setPageNum] = useState(1);
  const [customerListState, setCustomerListState] = useState({
    isLoading: true,
    showLoaderOnClick: false,
    error: "",
    noRecords: false,
    showLoaderOnSave: false,
  });
  const [formData, setFormData] = useState(initialFields);

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchPaymentTerm) {
      filterParams.push({
        field: "PaymentTermID",
        condition: "eq",
        filterValues: [filterState.searchPaymentTerm.value],
      });
    }
    if (filterState.searchStatus) {
      filterParams.push({
        field: "UserStatus",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    return {
      filters: filterParams.length > 0 ? JSON.stringify(filterParams) : [],
      searchString: filterState.searchName || "",
    };
  };

  useEffect(() => {
    getCustomerList(dispatch, {
      pageNum: pageNum,
      filters: filterState.filterParams.filters,
      searchString: filterState.searchName,
    });
    return () => {
      dispatch({ type: customerList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    getPaymentTermList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (customersList?.code === 200) {
      setCustomerListState({
        isLoading: false,
        error: "",
        noRecords: customersList?.data?.customer?.length === 0,
        showLoaderOnClick: false,
        showLoaderOnSave: false,
      });
    } else if (customersList?.code || customersList?.message) {
      setCustomerListState({
        isLoading: false,
        error:
          customersList?.message ||
          "Failed to load the manufacturers list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
        showLoaderOnSave: false,
      });
    }
  }, [customersList]);

  const formattedCustomerList =
    customersList?.data?.customer?.map((item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
            (customersList?.data?.noOfRecordsPerPage ||
              DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    }) ?? [];

  const notify = (type, message) => {
    //type is either success or danger
    const options = {
      place: "tr", //tr=top-right corner
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5, //notification closes automatically after 5 seconds
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const handleSearch = () => {
    if (
      !filterState.searchName.trim() &&
      !filterState.searchPaymentTerm &&
      !filterState.searchStatus
    ) {
      // All filters are empty — reset the applied filter (same as Clear)
      if (
        filterState.filterParams.length > 0 ||
        JSON.stringify(buildFilterParams())
      ) {
        handleClear();
      }
      return;
    }

    const newfilterParams = buildFilterParams();
    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setCustomerListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
        isLoading: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "firstName":
        return !value
          ? "Please enter customer first name"
          : value.length < 2
            ? "First name must be at least 2 characters"
            : value.length > 50
              ? "First name must be less than 50 characters"
              : "";

      case "lastName":
        return !value
          ? "Please enter customer last name"
          : value.length < 2
            ? "Last name must be at least 2 characters"
            : value.length > 50
              ? "Last name must be less than 50 characters"
              : "";

      case "email":
        return !value
          ? "Please enter email address"
          : value.length > 100
            ? "Email must be less than 100 characters"
            : !REGEX.EMAIL_REGEX.test(value)
              ? "Please enter a valid email address"
              : "";

      case "contact":
        return !value
          ? "Please enter contact number"
          : !REGEX.CONTACT_REGEX.test(value)
            ? "Please enter a valid 9-digit contact number"
            : "";

      // case "vatID":
      //   return !value
      //     ? "Please enter VAT ID"
      //     : !REGEX.VAT_ID_REGEX.test(value)
      //       ? "Please enter a valid 15 digits vat id"
      //       : "";

      // case "clientID":
      //   return !value
      //     ? "Please enter Client ID"
      //     : value.length > 100
      //       ? "Client ID must be less than 100 characters"
      //       : "";

      case "paymentTerm":
        return !value ? "Please select payment term" : "";

      case "companyName":
        return !value
          ? "Please enter company name"
          : value.length < 2
            ? "Name must be at least 2 characters"
            : value.length > 200
              ? "Name must be less than 200 characters"
              : "";

      default:
        return "";
    }
  };

  const handleChange = (fieldName, newValue) => {
    //this updates formData.value
    setFormData((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName], //in future if we add property then that will be unchanged, only value, error will be changed
        value: newValue,
        error: "",
      },
    }));
  };

  const handleOnBlur = (name, value) => {
    //this updates formData.error for single input field
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateAllFields = () => {
    //this updates formData.error for all input fields
    let updated = { ...formData };

    Object.keys(updated).forEach((field) => {
      const rawValue =
        field === "paymentTerm"
          ? updated[field].value?.value
          : updated[field].value; //took add customer fields values where value.value is only for paymentTerm because it has dropdown and that value will be passed in getValidationError for validaion

      const error = getValidationError(field, rawValue);

      updated[field] = {
        ...updated[field],
        error, //formData's error field gets updated which we get from getValidationError
      };
    });

    setFormData(updated);

    return Object.values(updated).every((item) => item.error === ""); //checks if every error field is empty in formData
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateAllFields();

    if (!isValid) {
      return;
    }
    let res;
    try {
      setCustomerListState((prev) => ({ ...prev, showLoaderOnSave: true }));

      const bodyData = {
        firstName: formData.firstName.value,
        lastName: formData.lastName.value,
        email: formData.email.value,
        phoneNo: formData.contact.value,
        address: formData.address.value,
        vatID: formData.vatID.value,
        clientID: formData.clientID.value,
        roleID: 4,
        paymentTermID: formData.paymentTerm.value?.value,
        companyName: formData.companyName.value,
      };
      res = await addCustomer(bodyData);
      if (res.code === 200) {
        notify("success", res.message || "Customer added successfully!");
        handleCloseModal();
        setFormData(initialFields);
        if (pageNum === 1) {
          getCustomerList(dispatch, 1);
        } else {
          setPageNum(1);
        }
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg || JSON.stringify(res?.message)
          : res?.message || "Failed to add customer!";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Failed to add customer!");
    } finally {
      setCustomerListState((prev) => ({ ...prev, showLoaderOnSave: false }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // e.preventDefault();
      handleSearch();
    }
  };

  const handleClear = () => {
    setCustomerListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      searchPaymentTerm: null,
      searchStatus: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const columns = [
    { Header: "Sr. no.", accessor: "srNo", centerAlign:true,className: "col-srNo" },
    {
      Header: "Name",
      accessor: "name",
      className: "col-cust-name",
      Cell: ({ row }) => {
        const { firstName = "", lastName = "" } = row?.original || {};
        return <span>{`${firstName} ${lastName}`.trim()}</span>;
      },
    },
    { Header: "Company Name", accessor: "companyName", className: "col-company", Cell: ({ value }) => <span>{value || "-"}</span> },
    {
      Header: "Email", accessor: "email", className: "col-email",
      Cell: ({ value }) => (
        <div>
          {value || "-"}
        </div>
      )
    },
    {
      Header: "Contact Number", accessor: "phoneNo", className: "col-phone",
      Cell: ({ value }) => (
        <div>
          {value || "-"}
        </div>
      ),
    },
    {
      Header: "Address",
      accessor: "address",
      className: "col-cust-address",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Payment Term",
      Cell: ({ row }) => row.original.paymentTerm?.term || "",
      className: "col-term"
    },
    { Header: "VAT ID", accessor: "vatID", className: "col-vat-client" },
    { Header: "Client ID", accessor: "clientID", className: "col-vat-client" },
    {
      Header: "Status",
      accessor: "status",
      className: "col-cust-status",
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Action",
      accessor: "actions",
      // className: "text-center",
      className: "col-useraction",
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="actions-right d-flex justify-content-start">
          <div>
            <Button
              className="btn-icon icon-info"
              color="link"
              id={`edit${row.original.ID}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();

                const encodedId = urlEncodedId(row.original.ID);

                window.open(
                  `/admin/customers/${encodedId}/details?tab=details`,
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
            >
              <i className="tim-icons icon-pencil tim-md" />
            </Button>
            <UncontrolledTooltip delay={0} target={`edit${row.original.ID}`}>
              Edit
            </UncontrolledTooltip>
          </div>
        </div>
      ),
    },
  ];

  const handleAddCustomer = () => {
    //this opens rightpanelcard
    setIsOpen(true);
  };
  const handleCloseModal = () => {
    //this closes rightpanelcard and sets formData empty
    setIsOpen(false);
    setFormData(initialFields);
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  return (
    <>
      <div className="rna-wrapper">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between mb-2">
                <CardTitle tag="h3" className="font-weight-normal">
                  Customers
                </CardTitle>
                <Button
                  className="btn-round btn-style"
                  color="default"
                  size="md"
                  onClick={handleAddCustomer}
                >
                  <i className="fa fa-plus mr-1" />
                  Add Customer
                </Button>
              </CardHeader>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name, email, VAT ID or client ID"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={paymentTermOptions.map((term) => ({
                        value: String(term.ID),
                        label: term.term,
                      }))}
                      placeholder="Payment term"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={filterState.searchPaymentTerm}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchPaymentTerm: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={statusOptions}
                      placeholder="Status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {customerListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {(filterState.filterParams?.filters?.length > 0 ||
                      filterState.filterParams?.searchString?.trim()) && (
                      <Button
                        color="default"
                        className="d-flex align-items-center justify-content-center mt-2 mb-3 filter-btn btn-simple"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 mb-3 mx-1">
                  {customersList?.data?.totalRecords ? (
                    <>
                      {filterState.filterParams?.filters?.length > 0 ||
                      filterState.filterParams?.searchString?.trim() ? (
                        <span className="font-weight-bold">
                          Showing {customersList?.data?.totalFilteredRecords} of{" "}
                          {customersList?.data?.totalRecords} Customers
                        </span>
                      ) : (
                        <span className="font-weight-bold">
                          Total Customers : {customersList?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : (
                    ""
                  )}
                </div>
                <div className="table-responsive">
                  {customerListState.isLoading ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </div>
                  ) : customerListState.error ? (
                    <div className="loaderContainer text-muted d-flex flex-column">
                      <h4 className="text-muted">
                        Failed to load customer list
                      </h4>
                      <span className="validation-error">
                        {customerListState.error}
                      </span>
                    </div>
                  ) : customerListState.noRecords ? (
                    <div className="loaderContainer text-muted">
                      No customers found.
                    </div>
                  ) : (
                    <CommonTable
                      data={formattedCustomerList}
                      resizable={false}
                      columns={columns}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      showPaginationBottom={true}
                      className="-striped -highlight table-hover"
                      rowClick={(rowData) => {
                          const encodedId = urlEncodedId(rowData.ID);

                          window.open(
                            `/admin/customers/${encodedId}/details?tab=details`,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                      totalPages={Math.ceil(
                        customersList?.data?.totalFilteredRecords /
                          customersList?.data?.noOfRecordsPerPage,
                      )}
                      setPageNum={setPageNum}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <RightPanelCard isOpen={isOpen} onClose={handleCloseModal}>
        <Card className="mb-0">
          <CardHeader
            className="d-flex align-items-center justify-content-between mb-2"
            tag="h4"
            toggle={handleCloseModal}
          >
            Add Customer
            <i
              className="fa fa-close text-muted pointer"
              onClick={handleCloseModal}
            />
          </CardHeader>

          <CardBody>
            <Label className="customer-title">Company Details</Label>
            <Row>
              <Col md="6" className="mb-3 mt-2">
                <FormGroup
                  className={`has-label ${formData.companyName.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>Company Name <span className="validation-error">*</span>
                  </Label>
                  <Input
                    name="companyName"
                    type="text"
                    placeholder="Enter company name"
                    value={formData.companyName.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    maxLength={200}
                  />

                  {formData.companyName.error && (
                    <small className="validation-error">
                      {formData.companyName.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-2 mt-2">
                <FormGroup className="d-flex flex-column align-items-start">
                  <Label>Address</Label>
                  <Input
                    name="address"
                    type="text"
                    placeholder="Enter address"
                    value={formData.address.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    maxLength={100}
                  />
                </FormGroup>
              </Col>

              <Col md="6" className="mb-1">
                <FormGroup
                  className={`has-label ${formData.vatID.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    VAT ID <span className="text-muted text-sm">(Optional)</span>
                  </Label>
                  <Input
                    name="vatID"
                    type="text"
                    placeholder="Enter VAT ID"
                    value={formData.vatID.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    maxLength={15}
                  />

                  {formData.vatID.error && (
                    <small className="validation-error">
                      {formData.vatID.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <hr />
            <Label className="customer-title">Account Owner</Label>
            <Row>
              <Col md="6" className="mb-3 mt-2">
                <FormGroup
                  className={`has-label ${
                    formData.firstName.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
                >
                  <Label>
                    First Name <span className="validation-error">*</span>
                  </Label>
                  <Input
                    name="firstName"
                    type="text"
                    placeholder="Enter first name"
                    value={formData.firstName.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    maxLength={50}
                  />

                  {formData.firstName.error && (
                    <small className="validation-error">
                      {formData.firstName.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3 mt-2">
                <FormGroup
                  className={`has-label ${
                    formData.lastName.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Last Name <span className="validation-error">*</span>
                  </Label>
                  <Input
                    name="lastName"
                    type="text"
                    placeholder="Enter last name"
                    value={formData.lastName.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    maxLength={50}
                  />

                  {formData.lastName.error && (
                    <small className="validation-error">
                      {formData.lastName.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${
                    formData.email.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Email Address <span className="validation-error">*</span>
                  </Label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Enter email address"
                    value={formData.email.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    maxLength={100}
                  />

                  {formData.email.error && (
                    <small className="validation-error">
                      {formData.email.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-2">
                <FormGroup
                  className={`has-label ${
                    formData.contact.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Contact Number <span className="validation-error">*</span>
                  </Label>
                  <InputGroup className="input-group">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText className="d-flex align-items-center">
                        <span className="mr-1">🇸🇦</span> +966
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      name="contact"
                      type="tel"
                      placeholder="Enter contact number"
                      value={formData.contact.value}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                      maxLength={9}
                    />
                  </InputGroup>
                  {formData.contact.error && (
                    <small className="validation-error">
                      {formData.contact.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${
                    formData.paymentTerm.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Payment Term <span className="validation-error">*</span>
                  </Label>
                  <Select
                    className={`text-left ${
                      formData.paymentTerm.error ? "is-invalid" : ""
                    }`}
                    classNamePrefix="react-select"
                    options={paymentTermOptions.map((term) => ({
                      value: term.ID,
                      label: term.term,
                    }))}
                    placeholder="Select payment term"
                    styles={customStyle}
                    value={formData.paymentTerm.value}
                    onChange={(selected) =>
                      handleChange("paymentTerm", selected)
                    }
                    onBlur={() =>
                      handleOnBlur("paymentTerm", formData.paymentTerm.value)
                    }
                  />

                  {formData.paymentTerm.error && (
                    <small className="validation-error">
                      {formData.paymentTerm.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${
                    formData.clientID.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Client ID 
                    {/* <span className="validation-error">*</span> */}
                  </Label>
                  <Input
                    name="clientID"
                    type="text"
                    placeholder="Enter client ID"
                    value={formData.clientID.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    maxLength={100}
                  />

                  {formData.clientID.error && (
                    <small className="validation-error">
                      {formData.clientID.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </CardBody>

          <CardFooter className="d-flex justify-content-end mb-0">
            <Button
              color="secondary"
              onClick={handleCloseModal}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              color="default"
              type="submit"
              onClick={handleSubmit}
              disabled={Object.values(formData).some(
                (item) => item.error !== "",
              )}
            >
              {customerListState.showLoaderOnSave ? (
                <i className="fa fa-spinner fa-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </CardFooter>
        </Card>
      </RightPanelCard>
    </>
  );
}

export default CustomerList;
